import PropTypes from "prop-types";
import * as React from "react";
import fetcher, { HTTPMethods } from "../../../hooks/fetcher";
import { DeleteIcon, Dialog, ImageIcon, List, Spinner } from "../../../themeV2";
import GlassPill from "../../GlassPill/GlassPill";
import css from "./ShopHero.module.css";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

const BannerUpload = React.memo(function BannerUpload({
  influencerId,
  bannerUrl,
  refresh,
}) {
  const inputRef = React.useRef(null);
  const [isUploading, setIsUploading] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [error, setError] = React.useState(null);

  const hasBanner = Boolean(bannerUrl);

  const handleDialogOpen = React.useCallback(() => {
    setError(null);
    setDialogOpen(true);
  }, []);

  const handleDialogClose = React.useCallback(
    (open) => {
      if (!isUploading && !open) {
        setDialogOpen(false);
      }
    },
    [isUploading],
  );

  const handleChangeBanner = React.useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleFileSelect = React.useCallback(
    async (evt) => {
      const file = evt.target.files?.[0];

      // Reset input so re-selecting the same file triggers onChange
      if (inputRef.current) {
        inputRef.current.value = "";
      }

      if (!file) {
        return;
      }

      if (!ACCEPTED_TYPES.includes(file.type)) {
        setError("Please select a JPEG, PNG, or WebP image.");
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        setError("Image must be under 10 MB.");
        return;
      }

      setError(null);
      setIsUploading(true);

      try {
        const base64 = await readFileAsBase64(file);

        await fetcher(`/api/influencer/${influencerId}`, {
          method: HTTPMethods.PUT,
          body: JSON.stringify({ banner: base64 }),
        });

        await refresh();
        setDialogOpen(false);
      } catch {
        setError("Failed to upload banner. Please try again.");
      } finally {
        setIsUploading(false);
      }
    },
    [influencerId, refresh],
  );

  const handleRemove = React.useCallback(async () => {
    setError(null);
    setIsUploading(true);

    try {
      await fetcher(`/api/influencer/${influencerId}`, {
        method: HTTPMethods.PUT,
        body: JSON.stringify({
          banner_url: null,
          banner_source_url: null,
        }),
      });

      await refresh();
      setDialogOpen(false);
    } catch {
      setError("Failed to remove banner. Please try again.");
    } finally {
      setIsUploading(false);
    }
  }, [influencerId, refresh]);

  return (
    <>
      <GlassPill
        className={css.bannerEditPill}
        onClick={handleDialogOpen}
        aria-label="Change banner"
      >
        <ImageIcon /> Edit banner
      </GlassPill>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={handleFileSelect}
        aria-label="Upload banner image"
      />

      <Dialog.Root open={dialogOpen} onOpenChange={handleDialogClose}>
        <Dialog.Popup size="sm">
          <Dialog.Title>Change banner</Dialog.Title>
          <Dialog.Close />

          <List.Root className={css.dialogOptionList}>
            <List.ItemButton
              leading={isUploading ? <Spinner size="small" /> : <ImageIcon />}
              secondary="JPEG, PNG, or WebP — max 10 MB"
              onClick={handleChangeBanner}
              disabled={isUploading}
            >
              {hasBanner ? "Upload new image" : "Upload image"}
            </List.ItemButton>

            {hasBanner && (
              <List.ItemButton
                leading={<DeleteIcon />}
                onClick={handleRemove}
                disabled={isUploading}
              >
                Remove banner
              </List.ItemButton>
            )}
          </List.Root>

          {Boolean(error) && <p className={css.dialogError}>{error}</p>}
        </Dialog.Popup>
      </Dialog.Root>
    </>
  );
});

BannerUpload.propTypes = {
  influencerId: PropTypes.number.isRequired,
  bannerUrl: PropTypes.string,
  refresh: PropTypes.func.isRequired,
};

export default BannerUpload;
